import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { toggleModal, toggleSpinner } from 'apps/auth/actions/appAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, removeFieldError, setFieldError, setNewFields } from 'apps/auth/actions/formAction';
import { changePhoneCountryCode } from 'apps/auth/actions/loginAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import PhoneContacts from 'apps/auth/components/common/PhoneContacts/PhoneContacts';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import SelectCountryWithCodeScreen from 'apps/auth/components/common/search/SelectCountryWithCodeScreen';
import { AMOUNT_TO_CCY, PHONE } from 'apps/auth/constants/fieldsName';
import { REQUEST_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_REQUEST_CHECK_PHONE_NUMBER, AUTH_REQUEST_CREATE } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { countryPropType } from 'apps/auth/helpers/customPropTypes';
import { timer } from 'apps/auth/helpers/utils';
import { useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';
import { ApiService } from 'apps/auth/services/apiService';

import InputPhoneWithDropdown from '../form/InputPhoneWithDropdown';
import { hasContacts } from '../form-multi/utils';

const getPhonePrefix = (countries, countryCode) => {
    const phonePrefix = countries?.find(({ code }) => code === countryCode)?.phonePrefix;
    return phonePrefix ? parseInt(phonePrefix) : '';
};

const FromFields = (props) => {
    const { requestFieldsFromTitle, mobileNumberOfRequestee, gotItButton } = usePageMessages();
    const { fromCountry, fromCountries = [] } = useExtra() ?? {};
    const fields = useFields(props.formName);

    const timerId = useRef(null);
    const userInputRef = useRef('');
    const controllerRef = useRef(null);
    const popupRef = useRef(null);
    const [iso2, updateIso2] = useState(fromCountry?.code);
    const [prefix, setPrefix] = useState(() => getPhonePrefix(fromCountries, fromCountry?.code));
    const [userInputWithoutPrefix, setUserInputWithoutPrefix] = useState(fields[PHONE]?.value ? fields[PHONE]?.value.replace(prefix, '') : '');

    useEffect(() => {
        updateIso2(fromCountry?.code);
        const newPrefix = getPhonePrefix(fromCountries, fromCountry?.code);
        if (newPrefix) {
            setPrefix(newPrefix);
        }
    }, [fromCountry?.code]);

    const closeModal = () => props.toggleModal(false);

    const updateFieldsAndMessages = (json) => {
        if (json?.extra) {
            props.addExtras(json?.extra);
        }
        const newFieldsFromJson = json?.forms[props.formName]?.fields;
        if (newFieldsFromJson) {
            props.setNewFields(props.formName, newFieldsFromJson);
        }
        if (json?.messageBodyMessages) {
            props.updatePageMessages(json.messageBodyMessages);
        }
    };

    const onCountryItemClick = (code, newPrefix) => {
        const prefixWithoutPlus = prefix.toString().replace(/\+/, '');
        const newValue = fields[PHONE]?.value?.trim().replace(prefixWithoutPlus, '');
        const newFields = {
            ...fields,
            phone: {
                ...fields.phone,
                value: newPrefix + newValue,
            },
        };
        updateIso2(code);
        setPrefix(newPrefix);
        props.toggleModal(false);
        props.changePhoneCountryCode(code);

        ApiService.fetch(
            `${AUTH_REQUEST_CREATE}/${newFields[AMOUNT_TO_CCY]?.value}/${code}`,
            { fields: newFields, ...props },
            (json) => {
                updateFieldsAndMessages(json);
            },
            null,
            popupRef,
        );
    };

    const getUserInputWithoutPrefix = (value) => setUserInputWithoutPrefix(value);

    const setPhoneField = (value) => {
        const newFields = { ...fields, phone: { ...fields.phone, value: value } };

        return apiFetchData(AUTH_REQUEST_CHECK_PHONE_NUMBER, newFields, controllerRef).then((json) => {
            const errors = Object.keys(json.errors);
            const hasErrors = errors?.length > 0;

            if (hasErrors) {
                errors.forEach((name) => props.setFieldError(props.formName, name, json.errors[name]));
            } else {
                props.removeFieldError(props.formName, PHONE);

                const code = json.extra?.fromCountry?.code;
                const newPrefix = json.extra?.fromCountry?.phonePrefix;

                if (code) {
                    updateIso2(code);
                    props.changePhoneCountryCode(code);
                }

                if (newPrefix) {
                    setPrefix(newPrefix);
                }

                updateFieldsAndMessages(json);
            }

            props.disabledSubmitButton(hasErrors);

            return {
                success: !hasErrors,
                errorMessage: json.errors.phone?.body || '',
            };
        });
    };

    const getServer = (value) => (timerId.current = timer(timerId.current, () => setPhoneField(value)));

    const onChangeHandler = (value) => {
        const valueNotEmpty = value?.length > 5;
        props.disabledSubmitButton(true);
        if (valueNotEmpty) {
            getServer(value);
        }
    };

    const onBlurHandler = (value, field) => {
        const valueNotEmpty = value?.length > 5;
        if (valueNotEmpty && field?.isError) {
            props.setFieldError(props.formName, field.name, field.errorMessage);
        }
        if (valueNotEmpty && !field?.isError) {
            getServer(value);
        }
    };

    const showCountryModal = () => {
        props.toggleModal(
            true,
            <SelectCountryWithCodeScreen
                countryWithCode={true}
                isRequest={true}
                iso2={iso2}
                requestCountries={fromCountries}
                onCountryItemClick={onCountryItemClick}
                closeModal={closeModal}
            />,
        );
    };

    const onContactPick = async (phone) => {
        props.toggleSpinner(true);

        const normalizedPhoneNumber = Number(phone.toString().replace(/[^\d]/g, '')).toString();
        const { success, errorMessage } = await setPhoneField(normalizedPhoneNumber);

        if (!success && errorMessage) {
            props.toggleSpinner(false);
            popupRef.current?.open(
                <SimplePopup text={errorMessage} popupRef={popupRef}>
                    <PopupButton
                        iconName="check"
                        text={gotItButton}
                        onClick={() => {
                            popupRef.current?.close();
                        }}
                    />
                </SimplePopup>,
            );
            return;
        }

        props.switchToNextScreen();
        props.toggleSpinner(false);
    };

    return (
        <>
            <HeaderPanel title={requestFieldsFromTitle} subtitle={mobileNumberOfRequestee} />
            <Row indent="adaptive-32-48">
                <InputPhoneWithDropdown
                    field={fields[PHONE]}
                    formName={props.formName}
                    iso2={iso2}
                    submitByEnter={true}
                    submitRef={props.submitRef}
                    userInputRef={userInputRef}
                    changeFormFieldValue={props.changeFormFieldValue}
                    prefix={prefix}
                    getUserInputWithoutPrefix={getUserInputWithoutPrefix}
                    userInputWithoutPrefix={userInputWithoutPrefix}
                    onChangeHandler={onChangeHandler}
                    onBlurHandler={onBlurHandler}
                    showCountryModal={showCountryModal}
                    focus={true}
                />
            </Row>
            {hasContacts() && <PhoneContacts submitRef={props.submitRef} formName={REQUEST_FORM_NAME} onContactPick={onContactPick} />}
            <PopupNew ref={popupRef} />
        </>
    );
};
FromFields.propsType = {
    formName: PropTypes.string.isRequired,
    fromCountry: countryPropType,
};
export default connect(null, {
    addExtras,
    updatePageMessages,
    setNewFields,
    changeFormFieldValue,
    changePhoneCountryCode,
    toggleModal,
    toggleSpinner,
    setFieldError,
    removeFieldError,
})(FromFields);
